<template>
  <div class="about">
    <section class="about-hero">
      <div class="about-hero-bg">
        <img :src="img('gen/hero-about.jpg')" alt="关于皓辰"/>
        <div class="about-mask"></div>
        <div class="grid-bg"></div>
      </div>
      <div class="page about-hero-inner">
        <span class="eyebrow reveal" v-reveal>About Us</span>
        <h1 class="h1 reveal" v-reveal="{ delay: 80 }">
          关于<span class="grad-text">皓辰科技</span>
        </h1>
        <p class="lead reveal" v-reveal="{ delay: 160 }">
          一家把技术带到田间、车间与办公室的产业数字化服务商。
        </p>
      </div>
    </section>

    <!-- 公司简介 -->
    <section class="sec">
      <div class="page">
        <div class="intro">
          <div class="intro-text reveal" v-reveal>
            <span class="eyebrow">Company Profile</span>
            <h2 class="h2">{{ company.name }}</h2>
            <p class="lead">{{ company.intro }}</p>
            <p class="lead intro-more">
              公司以「让产业现场长出智能」为使命，坚持从真实生产现场出发定义技术方案：
              能用手动挡解决的，不硬塞自动挡；能用一台网关搞定的，不铺三套系统。
              我们相信，能被一线工人和农户用起来的系统，才是好系统。
            </p>
            <div class="intro-tags">
              <span class="tag">智慧农业</span>
              <span class="tag">工业物联网</span>
              <span class="tag">企业服务</span>
              <span class="tag">全栈自研</span>
            </div>

            <dl class="intro-facts">
              <div>
                <dt>公司全称</dt>
                <dd>{{ company.name }}</dd>
              </div>
              <div>
                <dt>成立时间</dt>
                <dd>{{ company.foundedFull }}</dd>
              </div>
              <div>
                <dt>办公地址</dt>
                <dd>{{ company.address }}</dd>
              </div>
              <div>
                <dt>联系电话</dt>
                <dd><a :href="`tel:${company.phone}`">{{ company.phone }}</a></dd>
              </div>
              <div>
                <dt>电子邮箱</dt>
                <dd><a :href="`mailto:${company.email}`">{{ company.email }}</a></dd>
              </div>
              <div>
                <dt>{{ company.wechat.label }}</dt>
                <dd>{{ company.wechat.value }}（二维码见「联系我们」页）</dd>
              </div>
              <div>
                <dt>在线演示</dt>
                <dd>
                  <a :href="demo.url" target="_blank" rel="noopener noreferrer">{{ demo.url }}</a>
                  <i>{{ demo.credentialNote }}</i>
                </dd>
              </div>
            </dl>
          </div>

          <div class="intro-cards">
            <div v-for="(v, i) in values" :key="v.title" class="value-card card card-pad reveal" v-reveal="{ delay: i * 100 }">
              <div class="value-icon"><IconSvg :name="v.icon"/></div>
              <h3>{{ v.title }}</h3>
              <p>{{ v.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 发展历程 -->
    <section class="sec sec-tint" id="history">
      <div class="page">
        <SectionHeading
          eyebrow="Milestones"
          title="一步步走到现在"
          desc="2025 年 6 月成立至今，三条业务方向与端到端交付能力逐步成型。"
        />

        <div class="timeline">
          <div
            v-for="(m, i) in milestones"
            :key="`${m.year}-${i}`"
            class="tl-item reveal"
            :class="{ 'is-right': i % 2 === 1 }"
            v-reveal="{ delay: i * 80 }"
          >
            <span class="tl-dot"></span>
            <div class="tl-card card card-pad">
              <span class="tl-year">{{ m.year }}</span>
              <h3>{{ m.title }}</h3>
              <p>{{ m.desc }}</p>
            </div>
          </div>
          <span class="tl-axis"></span>
        </div>
      </div>
    </section>

    <TechMarquee/>

    <!-- 业务线入口 -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="Business"
          title="我们提供的四条业务线"
          desc="既可以单点切入，也可以整体规划、分批落地。"
        />
        <div class="biz-entries">
          <router-link
            v-for="l in lines"
            :key="l.key"
            :to="l.path"
            class="biz-entry reveal"
            v-reveal
            :style="{ '--accent': l.accent }"
          >
            <div class="biz-entry-top">
              <h3>{{ l.name }}</h3>
              <span class="biz-entry-en">{{ l.en }}</span>
            </div>
            <p>{{ l.tagline }}</p>
            <span class="biz-entry-link">了解详情 <IconSvg name="arrow-right"/></span>
          </router-link>
        </div>
      </div>
    </section>

    <CtaBand
      eyebrow="Contact"
      title="聊聊你的产业现场"
      desc="无论是新建项目还是存量改造，先做一次技术沟通，判断清楚再动手。"
      :bg="img('gen/cta-bg.jpg')"
    />
  </div>
</template>

<script>
import SectionHeading from '@/components/common/SectionHeading'
import IconSvg from '@/components/common/IconSvg'
import TechMarquee from '@/components/home/TechMarquee'
import CtaBand from '@/components/common/CtaBand'
import img from '@/utils/assets'
import { company, values, milestones, businessLines, demo } from '@/data/site'

export default {
  name: 'AboutPage',
  components: { SectionHeading, IconSvg, TechMarquee, CtaBand },
  data() {
    return { company, values, milestones, lines: businessLines, demo, img }
  }
}
</script>

<style lang="scss" scoped>
.about-hero {
  position: relative;
  min-height: 58vh;
  display: flex;
  align-items: center;
  padding: calc(var(--nav-h) + 40px) 0 70px;
  overflow: hidden;
}

.about-hero-bg {
  position: absolute;
  inset: 0;

  img { width: 100%; height: 100%; object-fit: cover; opacity: 0.45; }

  .about-mask {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 60% at 25% 45%, rgba(34, 224, 255, 0.16), transparent 70%),
      linear-gradient(180deg, rgba(3, 7, 17, 0.88), rgba(3, 7, 17, 0.72) 50%, rgba(3, 7, 17, 0.98));
  }
}

.about-hero-inner { position: relative; z-index: 2; }
.about-hero .h1 { margin-top: 20px; }
.about-hero .lead { margin-top: 20px; font-size: 17px; }

/* 简介 */
.intro { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 54px; align-items: start; }
.intro-text .h2 { margin-top: 16px; }
.intro-text .lead { margin-top: 22px; }
.intro-more { margin-top: 18px; }
.intro-facts {
  margin: 30px 0 0;
  padding-top: 26px;
  border-top: 1px solid rgba(120, 176, 255, 0.12);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 28px;

  div { display: flex; flex-direction: column; gap: 5px; }
  dt { font-size: 12.5px; letter-spacing: 0.08em; color: var(--txt-3); }
  dd {
    margin: 0;
    font-size: 14.5px;
    color: var(--txt);
    word-break: break-all;
  }
  a {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    color: var(--cyan);
    border-bottom: 1px solid transparent;
    transition: border-color 0.3s;
  }
  a:hover { border-color: var(--cyan); }
  i { display: block; margin-top: 4px; font-style: normal; font-size: 12.5px; color: var(--txt-3); }
}

@media (max-width: 760px) {
  .intro-facts { grid-template-columns: minmax(0, 1fr); }
}

.intro-tags { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 28px; }

.intro-cards { display: flex; flex-direction: column; gap: 16px; }

.value-card {
  padding: 26px 26px 28px;
  &:hover .value-icon { color: var(--cyan); border-color: var(--cyan); }

  .value-icon {
    width: 46px; height: 46px; padding: 12px;
    border-radius: 12px;
    border: 1px solid var(--line-strong);
    color: var(--txt-2);
    background: rgba(255, 255, 255, 0.03);
    transition: all 0.4s;
  }

  h3 { margin-top: 18px; font-size: 18px; color: #fff; }
  p { margin-top: 10px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
}

/* 时间轴 */
.timeline {
  position: relative;
  padding: 10px 0;
}

.tl-axis {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(180deg, transparent, rgba(34, 224, 255, 0.45), transparent);
}

.tl-item {
  position: relative;
  width: 50%;
  padding-right: 46px;
  margin-bottom: 26px;

  &.is-right { margin-left: 50%; padding-right: 0; padding-left: 46px; }

  .tl-dot {
    position: absolute;
    top: 34px;
    right: -6px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--grad-main);
    box-shadow: 0 0 0 5px rgba(34, 224, 255, 0.12), 0 0 18px rgba(34, 224, 255, 0.8);
  }

  &.is-right .tl-dot { right: auto; left: -6px; }

  &:hover .tl-card { border-color: var(--line-strong); transform: translateY(-4px); }
}

.tl-card {
  padding: 24px 26px;

  .tl-year {
    display: inline-block;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: var(--cyan);
    margin-bottom: 10px;
  }

  h3 { font-size: 18px; color: #fff; }
  p { margin-top: 9px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
}

/* 业务线入口 */
.biz-entries { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; }

.biz-entry {
  padding: 28px 28px 30px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(150deg, rgba(12, 26, 52, 0.74), rgba(5, 12, 27, 0.58));
  transition: transform 0.45s, border-color 0.45s, box-shadow 0.45s;

  .biz-entry-top { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
  h3 { font-size: 20px; color: #fff; }
  .biz-entry-en { font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--txt-3); }
  p { margin-top: 12px; font-size: 14px; color: var(--accent-txt); }

  .biz-entry-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 22px;
    font-size: 14px;
    color: var(--txt-2);
    svg { width: 17px; height: 17px; transition: transform 0.4s; }
  }

  &:hover {
    transform: translateY(-6px);
    border-color: color-mix(in srgb, var(--accent) 45%, transparent);
    box-shadow: 0 26px 60px -34px var(--accent);

    .biz-entry-link { color: var(--accent-txt); }
    .biz-entry-link svg { transform: translateX(5px); }
  }
}

@media (max-width: 1080px) {
  .intro { grid-template-columns: minmax(0, 1fr); gap: 32px; }
  .biz-entries { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 760px) {
  .about-hero { min-height: auto; padding: calc(var(--nav-h) + 28px) 0 48px; }
  .biz-entries { grid-template-columns: minmax(0, 1fr); }
  .tl-axis { left: 6px; }
  .tl-item, .tl-item.is-right {
    width: 100%;
    margin-left: 0;
    padding-left: 30px;
    padding-right: 0;

    .tl-dot, &.is-right .tl-dot { left: 0; right: auto; top: 28px; }
  }
}
</style>
